import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Users, DollarSign, TrendingUp, Mail, Search, LogIn, Ban, CheckCircle2, Shield, Home, ListTodo } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import CompliancePanel from '../components/admin/CompliancePanel';
import AutoDeclineRuns from '../components/admin/AutoDeclineRuns';
import ConsentProofButton from '../components/compliance/ConsentProofButton';

export default function Admin() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [tab, setTab] = useState('couples');

  const load = useCallback(async () => {
    try {
      const [{ data: ov }, { data: us }] = await Promise.all([
        client.get('/admin/overview'),
        client.get('/admin/users', { params: { q: q || undefined, plan: filter || undefined, limit: 100 } }),
      ]);
      setOverview(ov);
      setUsers(us.users);
      setTotal(us.total);
      setLoading(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      setLoading(false);
    }
  }, [q, filter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (uid) => {
    try {
      const { data } = await client.get(`/admin/users/${uid}`);
      setDetail(data);
    } catch (err) {
      toast({ title: 'Could not load user', variant: 'destructive' });
    }
  };

  const impersonate = async (uid, email) => {
    if (!window.confirm(`Impersonate ${email}? Every action under this session is still logged against their user id.`)) return;
    try {
      const { data } = await client.post('/admin/impersonate', { user_id: uid });
      localStorage.setItem('wf_token', data.token);
      toast({ title: `Impersonating ${email}. Reload to drop back to your own account.` });
      window.location.href = '/dashboard';
    } catch (err) {
      toast({ title: 'Impersonate failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const setPlan = async (uid, plan) => {
    try {
      await client.post(`/admin/users/${uid}/plan`, { plan });
      toast({ title: `Plan set to ${plan}` });
      load();
      if (detail?.user?.id === uid) openDetail(uid);
    } catch (err) {
      toast({ title: 'Could not set plan', variant: 'destructive' });
    }
  };

  const toggleSuspend = async (uid, suspended) => {
    try {
      await client.post(`/admin/users/${uid}/suspend`, { suspended: !suspended, reason: suspended ? '' : 'Admin action' });
      toast({ title: suspended ? 'Account reactivated.' : 'Account suspended.' });
      load();
      if (detail?.user?.id === uid) openDetail(uid);
    } catch (err) {
      toast({ title: 'Suspend failed', variant: 'destructive' });
    }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 max-w-md border border-[#EFE3D8] text-center">
          <Shield className="w-10 h-10 text-[#7B2C3B] mx-auto mb-3" />
          <h1 className="font-serif text-2xl text-[#2A2A2A] mb-2">Admin only</h1>
          <p className="text-sm text-[#6A6A6A] mb-4">Your account isn&apos;t in the owner allowlist.</p>
          <Link to="/dashboard" className="text-xs px-4 py-2 rounded-full border border-[#7B2C3B] text-[#7B2C3B] inline-flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (loading || !overview) {
    return <div className="min-h-screen flex items-center justify-center text-[#6A6A6A]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading admin…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FBF7F2] p-6 lg:p-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-[#7B2C3B]">Operator console</p>
            <h1 className="font-serif text-4xl text-[#2A2A2A]">Yourweddingfriends admin</h1>
            <p className="text-sm text-[#6A6A6A]">Signed in as {user?.email}</p>
          </div>
          <Link to="/dashboard" className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#3A3A3A] inline-flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> My dashboard</Link>
        </div>

        <div className="inline-flex rounded-full bg-white border border-[#EFE3D8] p-1 mb-6 text-sm" data-testid="admin-tabs">
          {[['couples', 'Couples & revenue'], ['legal', 'Legal & compliance']].map(([k, label]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                    className={`px-4 py-1.5 rounded-full transition-colors ${tab === k ? 'bg-[#7B2C3B] text-white' : 'text-[#6A6A6A] hover:text-[#7B2C3B]'}`}>{label}</button>
          ))}
        </div>

        {tab === 'legal' ? <CompliancePanel toast={toast} /> : (<>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <Kpi icon={Users} label="Total couples" value={overview.total_users} />
          <Kpi icon={DollarSign} label="MRR" value={`$${overview.mrr_usd.toLocaleString()}`} sub={`ARR $${overview.arr_usd.toLocaleString()}`} />
          <Kpi icon={CheckCircle2} label="Paid couples" value={overview.paid_users} sub={Object.entries(overview.plan_counts).map(([k, v]) => `${k}: ${v}`).join(' · ')} />
          <Kpi icon={TrendingUp} label="Signups (30d)" value={overview.signups_30d} sub={`${overview.paid_signups_30d} paid`} />
          <Kpi icon={Mail} label="Invitations sent (30d)" value={overview.invitation_sends_30d} />
        </div>

        <OpenTasks onOpenCouple={openDetail} />

        <div className="bg-white rounded-2xl border border-[#EFE3D8] p-4">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8A7E] pointer-events-none" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or partner name"
                     data-testid="admin-search"
                     className="w-full pl-9 pr-3 py-2 rounded-full bg-[#FBF7F2] border border-[#EFE3D8] text-sm focus:outline-none focus:border-[#7B2C3B]" />
            </div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-white">
              <option value="">All plans</option>
              <option value="sweetheart">Sweetheart</option>
              <option value="beloved">Beloved</option>
              <option value="concierge">Concierge</option>
              <option value="wedding_pass">Wedding Pass</option>
            </select>
            <p className="text-xs text-[#6A6A6A] ml-auto">Showing {users.length} of {total}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#9A8A7E] border-b border-[#EFE3D8]">
                  <th className="px-3 py-2">Couple</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2">Wedding date</th>
                  <th className="px-3 py-2">Signed up</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#EFE3D8]/60 hover:bg-[#FBF7F2] cursor-pointer"
                      data-testid={`admin-user-${u.id}`} onClick={() => openDetail(u.id)}>
                    <td className="px-3 py-2 text-[#2A2A2A]">{[u.partner1, u.partner2].filter(Boolean).join(' & ') || '—'} {u.suspended && <span className="text-[9px] uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full ml-1">suspended</span>}</td>
                    <td className="px-3 py-2 text-[#3A3A3A]">{u.email}</td>
                    <td className="px-3 py-2 text-[#3A3A3A]"><span className="text-[10px] uppercase tracking-wider bg-[#F4E4DA] text-[#7B2C3B] px-2 py-0.5 rounded-full">{u.plan}</span></td>
                    <td className="px-3 py-2 text-[#6A6A6A]">{u.wedding_date || '—'}</td>
                    <td className="px-3 py-2 text-[#6A6A6A]">{u.created_at?.slice(0, 10) || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={(e) => { e.stopPropagation(); impersonate(u.id, u.email); }} data-testid={`admin-impersonate-${u.id}`}
                              className="text-xs px-2 py-1 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1 mr-1">
                        <LogIn className="w-3 h-3" /> Impersonate
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); toggleSuspend(u.id, u.suspended); }}
                              className={`text-xs px-2 py-1 rounded-full border inline-flex items-center gap-1 ${u.suspended ? 'border-emerald-300 text-emerald-700' : 'border-red-300 text-red-700'}`}>
                        <Ban className="w-3 h-3" /> {u.suspended ? 'Reactivate' : 'Suspend'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          <AutoDeclineRuns />
        </>)}
      </div>

      {detail && (
        <UserDetailDrawer detail={detail} onClose={() => setDetail(null)} onSetPlan={setPlan} onSuspend={toggleSuspend} onImpersonate={impersonate} />
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8]">
      <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E] inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
      <p className="font-serif text-2xl text-[#2A2A2A]">{value}</p>
      {sub && <p className="text-[10px] text-[#9A8A7E] truncate">{sub}</p>}
    </div>
  );
}

function OpenTasks({ onOpenCouple }) {
  const [tasks, setTasks] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/tasks/open');
      setTasks(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    await client.put(`/admin/notes/${t.id}`, { done: true });
    load();
  };

  if (tasks.length === 0 && !expanded) return null;
  return (
    <div className="bg-white rounded-2xl border border-[#EFE3D8] p-4 mb-6" data-testid="admin-open-tasks">
      <button onClick={() => setExpanded((x) => !x)} className="w-full flex items-center justify-between">
        <p className="text-sm font-medium text-[#2A2A2A] inline-flex items-center gap-2">
          <ListTodo className="w-4 h-4 text-[#7B2C3B]" /> Open operator tasks
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#7B2C3B] text-white">{tasks.length}</span>
        </p>
        <span className="text-xs text-[#9A8A7E]">{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded && (
        <ul className="mt-3 space-y-1.5">
          {tasks.length === 0 && <li className="text-xs text-[#9A8A7E] italic">All caught up ✨</li>}
          {tasks.map((t) => (
            <li key={t.id} className="flex items-start gap-2 p-2 rounded-lg border border-amber-200 bg-amber-50">
              <input type="checkbox" checked={false} onChange={() => toggle(t)}
                     data-testid={`open-task-done-${t.id}`}
                     className="mt-0.5 accent-[#7B2C3B]" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#2A2A2A] whitespace-pre-wrap">{t.body}</p>
                <p className="text-[10px] text-[#9A8A7E] mt-0.5">
                  {t.due_at ? `Due ${t.due_at} · ` : ''}
                  <button onClick={() => onOpenCouple(t.user.id)} className="underline hover:text-[#7B2C3B]">
                    {[t.user.partner1, t.user.partner2].filter(Boolean).join(' & ') || t.user.email}
                  </button>
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UserDetailDrawer({ detail, onClose, onSetPlan, onSuspend, onImpersonate }) {
  const { user, profile, counts } = detail;
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({ kind: 'note', body: '', due_at: '' });
  const [savingNote, setSavingNote] = useState(false);

  const loadNotes = useCallback(async () => {
    try {
      const { data } = await client.get(`/admin/users/${user.id}/notes`);
      setNotes(data);
    } catch { /* ignore */ }
  }, [user.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.body.trim()) return;
    setSavingNote(true);
    try {
      await client.post(`/admin/users/${user.id}/notes`, newNote);
      setNewNote({ kind: newNote.kind, body: '', due_at: '' });
      loadNotes();
    } finally { setSavingNote(false); }
  };
  const toggleDone = async (n) => {
    await client.put(`/admin/notes/${n.id}`, { done: !n.done });
    loadNotes();
  };
  const removeNote = async (n) => {
    if (!window.confirm('Delete this note?')) return;
    await client.delete(`/admin/notes/${n.id}`);
    loadNotes();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-end p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
          <h2 className="font-serif text-xl text-[#2A2A2A]">{[profile.partner1, profile.partner2].filter(Boolean).join(' & ') || user.email}</h2>
          <button onClick={onClose} className="text-[#9A8A7E] text-xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Account</p>
            <p className="text-[#2A2A2A]">{user.email}</p>
            <p className="text-xs text-[#6A6A6A]">Plan: <strong>{user.plan}</strong> · Signed up {user.created_at?.slice(0, 10)} · {user.auth_provider}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Wedding</p>
            <p className="text-[#2A2A2A]">{profile.weddingDate || 'No date yet'}</p>
            <p className="text-xs text-[#6A6A6A]">{profile.venue || '—'}{profile.location ? ` · ${profile.location}` : ''}</p>
            <p className="text-xs text-[#6A6A6A]">Budget: {profile.currency} {Number(profile.budgetTotal || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Usage</p>
            <ul className="text-xs text-[#3A3A3A] grid grid-cols-2 gap-1">
              {Object.entries(counts).map(([k, v]) => <li key={k}>{k}: <strong>{v}</strong></li>)}
            </ul>
          </div>

          <div className="pt-3 border-t border-[#EFE3D8]">
            <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E] mb-2">Notes & tasks ({notes.length})</p>
            <form onSubmit={addNote} className="space-y-2 bg-[#FBF7F2] p-2.5 rounded-xl border border-[#EFE3D8] mb-3">
              <div className="flex gap-1">
                {['note', 'task'].map((k) => (
                  <button type="button" key={k} onClick={() => setNewNote({ ...newNote, kind: k })}
                          data-testid={`note-kind-${k}`}
                          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${newNote.kind === k ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8] text-[#3A3A3A]'}`}>{k}</button>
                ))}
                {newNote.kind === 'task' && (
                  <input type="date" value={newNote.due_at} onChange={(e) => setNewNote({ ...newNote, due_at: e.target.value })}
                         data-testid="note-due"
                         className="ml-auto text-[11px] px-2 py-1 rounded-full border border-[#EFE3D8] bg-white" />
                )}
              </div>
              <textarea value={newNote.body} onChange={(e) => setNewNote({ ...newNote, body: e.target.value })}
                        rows={2} placeholder={newNote.kind === 'task' ? 'What needs doing? e.g. Call Sarah about catering' : 'Jot a quick note…'}
                        data-testid="note-body"
                        className="w-full px-2.5 py-1.5 rounded-lg border border-[#EFE3D8] bg-white text-xs resize-none focus:outline-none focus:border-[#7B2C3B]" />
              <button type="submit" disabled={savingNote || !newNote.body.trim()}
                      data-testid="note-save"
                      className="text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-white disabled:opacity-50">
                {savingNote ? 'Saving…' : (newNote.kind === 'task' ? 'Add task' : 'Add note')}
              </button>
            </form>
            {notes.length === 0 ? (
              <p className="text-xs text-[#9A8A7E] italic">No notes yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="notes-list">
                {notes.map((n) => (
                  <li key={n.id} className={`p-2 rounded-lg border ${n.done ? 'bg-emerald-50 border-emerald-200 opacity-70' : (n.kind === 'task' ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#EFE3D8]')}`}>
                    <div className="flex items-start gap-2">
                      {n.kind === 'task' && (
                        <input type="checkbox" checked={n.done} onChange={() => toggleDone(n)}
                               data-testid={`note-done-${n.id}`}
                               className="mt-0.5 accent-[#7B2C3B]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs text-[#2A2A2A] whitespace-pre-wrap ${n.done ? 'line-through text-[#6A6A6A]' : ''}`}>{n.body}</p>
                        <p className="text-[10px] text-[#9A8A7E] mt-0.5">
                          {n.kind === 'task' && n.due_at ? `Due ${n.due_at} · ` : ''}
                          {n.created_at?.slice(0, 10)} · {n.created_by}
                        </p>
                      </div>
                      <button onClick={() => removeNote(n)} className="text-[#9A8A7E] hover:text-red-600 text-xs">×</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pt-3 border-t border-[#EFE3D8] space-y-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E] mb-1">Comp a plan</p>
              <div className="flex flex-wrap gap-1.5">
                {['sweetheart', 'beloved', 'concierge', 'wedding_pass'].map((p) => (
                  <button key={p} onClick={() => onSetPlan(user.id, p)} data-testid={`admin-setplan-${p}`}
                          className={`text-xs px-2.5 py-1 rounded-full border ${user.plan === p ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8] text-[#3A3A3A]'}`}>{p}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => onImpersonate(user.id, user.email)} className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] inline-flex items-center gap-1"><LogIn className="w-3 h-3" /> Impersonate</button>
              <button onClick={() => onSuspend(user.id, user.suspended)}
                      className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1 ${user.suspended ? 'border-emerald-300 text-emerald-700' : 'border-red-300 text-red-700'}`}>
                <Ban className="w-3 h-3" /> {user.suspended ? 'Reactivate' : 'Suspend'}
              </button>
            </div>
            <ConsentProofButton testId="admin-account-consent-pdf" path={`/admin/compliance/consent-proof/account/${user.id}.pdf`} filename={`consent-account-${user.id.slice(0, 8)}.pdf`} label="Export account consent records (PDF)" />
          </div>
        </div>
      </div>
    </div>
  );
}
